import { useState, type ReactNode } from 'react';
import { Command } from 'cmdk';
import { Popover as PopoverPrimitive } from 'radix-ui';
import { Check, ChevronsUpDown, Loader2, Search, X } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Field } from './input';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Kurz rechts in der Zeile, in Mono: die Kundennummer. */
  detail?: string;
}

export interface ComboboxProps {
  label?: ReactNode;
  options: ComboboxOption[];
  value?: string;
  /** Leerer String, wenn die Auswahl entfernt wird. */
  onChange?: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  /** Text, wenn die Suche nichts findet. */
  emptyText?: string;
  error?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  /** Ohne sichtbares Label Pflicht. */
  'aria-label'?: string;
  /**
   * Serversuche: Die Combobox meldet jeden Suchbegriff, beim Öffnen einen
   * leeren, und filtert nicht selbst. `options` sind dann die Treffer, die die
   * App dazu geladen hat. Ohne `onSearch` filtert sie `options` selbst.
   */
  onSearch?: (begriff: string) => void;
  /** Nur mit `onSearch`: Die Suche läuft. */
  loading?: boolean;
  /** Text für `value`, solange es nicht unter den `options` steht, etwa ein vorbelegter Kunde. */
  selectedLabel?: string;
}

/**
 * Auswahl mit Suche (Popover + Command). Ersetzt SearchSelect aus crm-web, die
 * Props sind dieselben, damit die Aufrufer unverändert bleiben. Mit `onSearch`
 * sucht die App auf dem Server (Kunde wählen unter Tausenden).
 * (Ergänzung nach shadcn/ui, nicht im Design System.)
 */
export function Combobox({
  label,
  options,
  value,
  onChange,
  placeholder = 'Auswählen',
  searchPlaceholder = 'Suchen',
  emptyText = 'Keine Treffer',
  error,
  required,
  disabled,
  className,
  'aria-label': ariaLabel,
  onSearch,
  loading,
  selectedLabel,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const serverSuche = onSearch !== undefined;
  const [begriff, setBegriff] = useState('');
  // Mit Serversuche verschwindet die gewählte Option aus `options`, sobald
  // jemand weitersucht; ihr Text bleibt hier.
  const [gemerkt, setGemerkt] = useState<ComboboxOption | null>(null);
  const gewaehlt = value
    ? (options.find((o) => o.value === value) ??
      (gemerkt?.value === value ? gemerkt : undefined) ??
      (selectedLabel ? { value, label: selectedLabel } : undefined))
    : undefined;

  const oeffnen = (offen: boolean) => {
    setOpen(offen);
    if (offen && onSearch) {
      setBegriff('');
      onSearch('');
    }
  };

  const feld = (attrs: { id?: string; 'aria-invalid'?: boolean; 'aria-describedby'?: string }) => (
    <PopoverPrimitive.Root open={open} onOpenChange={oeffnen}>
      <div className="relative">
        <PopoverPrimitive.Trigger asChild disabled={disabled}>
          <button
            type="button"
            role="combobox"
            aria-expanded={open}
            aria-label={ariaLabel}
            {...attrs}
            className={cn(
              'flex h-control-md w-full cursor-pointer items-center gap-2 rounded-md border border-input bg-muted pl-3 pr-2 text-left text-field transition-[border-color,box-shadow] duration-150',
              'focus-visible:border-ring focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring aria-expanded:border-ring aria-expanded:ring-1 aria-expanded:ring-ring',
              'aria-invalid:border-danger disabled:cursor-not-allowed disabled:opacity-45',
              // Platz für das größere Entfernen-Kreuz auf Touch-Geräten
              gewaehlt && !required && !disabled && 'touch:pr-9',
            )}
          >
            <span className={cn('min-w-0 flex-1 truncate', !gewaehlt && 'text-muted-foreground')}>{gewaehlt ? gewaehlt.label : placeholder}</span>
            <ChevronsUpDown size={16} strokeWidth={1.75} aria-hidden className={cn('shrink-0 text-muted-foreground', gewaehlt && !required && 'invisible')} />
          </button>
        </PopoverPrimitive.Trigger>
        {gewaehlt && !required && !disabled && (
          <button
            type="button"
            aria-label="Auswahl entfernen"
            onClick={() => onChange?.('')}
            className="absolute right-2 top-1/2 inline-flex size-5 -translate-y-1/2 touch:right-1 touch:size-8 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X size={14} strokeWidth={1.75} aria-hidden />
          </button>
        )}
      </div>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={6}
          className="z-[2000] w-[var(--radix-popover-trigger-width)] min-w-56 overflow-hidden rounded-lg border border-border bg-popover text-popover-foreground shadow-md animate-ek-pop"
        >
          <Command loop shouldFilter={!serverSuche}>
            <div className="flex items-center gap-2 border-b border-border px-3">
              <Search size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-muted-foreground" />
              <Command.Input
                placeholder={searchPlaceholder}
                {...(serverSuche && {
                  value: begriff,
                  onValueChange: (v: string) => {
                    setBegriff(v);
                    onSearch(v);
                  },
                })}
                className="h-control-md w-full bg-transparent text-field outline-none placeholder:text-muted-foreground focus-visible:outline-none"
              />
              {serverSuche && loading && (
                <Loader2 size={16} strokeWidth={1.75} role="status" aria-label="Suche läuft" className="shrink-0 animate-spin text-muted-foreground" />
              )}
            </div>
            <Command.List className="max-h-64 overflow-y-auto p-1">
              <Command.Empty className="px-2 py-6 text-center t-small text-muted-foreground">
                {serverSuche && loading ? 'Wird gesucht …' : emptyText}
              </Command.Empty>
              {options.map((o) => (
                <Command.Item
                  key={o.value}
                  // Ohne eigenen Filter zählt nur die Eindeutigkeit, Namen können doppelt sein.
                  value={serverSuche ? o.value : o.label}
                  keywords={serverSuche ? undefined : [o.value]}
                  onSelect={() => {
                    setGemerkt(o);
                    onChange?.(o.value);
                    setOpen(false);
                  }}
                  className="flex h-8 cursor-default select-none items-center gap-2 rounded-sm px-2 text-sm outline-none touch:h-11 data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground"
                >
                  <span className="flex-1 truncate">{o.label}</span>
                  {o.detail && <span className="shrink-0 t-mono text-muted-foreground">{o.detail}</span>}
                  {o.value === value && <Check size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-highlight" />}
                </Command.Item>
              ))}
            </Command.List>
          </Command>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );

  if (!label) return <div className={cn('w-full', className)}>{feld({ 'aria-invalid': error ? true : undefined })}</div>;
  return (
    <Field label={label} error={error} required={required} className={cn('w-full', className)}>
      {(attrs) => feld(attrs)}
    </Field>
  );
}
